/**
 * WHETHER TO WARN THE USER ABOUT THIS ARV, AND WHAT TO SAY.
 *
 * Pulled out of the 6,000-line Results page for one reason: this is the logic that decides
 * whether the customer sees the warning that protects them, and inside a page component it
 * could only be tested through a DOM. Here it is arithmetic, and `__tests__/arvAlarm.test.js`
 * pins all nineteen branches of it.
 *
 * WHY IT IS ONE FUNCTION AND NOT TWO
 * ----------------------------------
 * Two things on the Results screen warn about the same number: the amber alarm box near the
 * top, and a one-line mark beside the ARV figure ~260 lines of JSX further down. The mark
 * exists because the box is correct in ORDER but one or two phone scrolls away, and the
 * reader who comes straight down to the figure is exactly the reader who acts on it.
 *
 * A mark next to the figure pointing at a notice that is not on screen would be worse than
 * no mark at all. So neither of them reimplements the rule -- both call `arvAlarmReason`,
 * and they cannot drift apart. Same reason the backend has `env_flags.env_flag`.
 *
 * WHAT IT WILL NOT DO
 * -------------------
 * It never renders reassurance. The validated half of the score rule only says a low score
 * means "unreliable"; it does not say a high score means "reliable" (the ">= 88 is safe"
 * half failed on its first new property, at 14.6% error). And the gate saying "supported"
 * only means the two sources agree with each other, which is not the same as being right.
 * So a clean result produces null -- no green seal, no badge.
 *
 * It does not show the confidence score, and it does not show the disagreement percentage
 * either: a number invites the reader to treat it as a measurement of how wrong the value
 * is, and neither of them is one. And no case suggests WHICH of the two sources to believe,
 * because the measurement does not know.
 */

// Below this confidence score the ARV is flagged to the user as unreliable.
//
// MEASURED, not chosen. Across 22 out-of-sample properties every valuation scoring 88 or
// above landed within 8.3%, and every error past 20% scored 86 or below. A 12-property
// validation then split the rule: the "< 88 is unreliable" half held (mean |error| 29.7%,
// worst 85.2%, 1 of 10 inside 8%), while the ">= 88 is safe" half failed on its first new
// property (14.6%). So this threshold is used in ONE direction only — to raise a warning,
// never to certify a value. That is why a score at or above it renders nothing at all
// rather than a reassurance. See BACKLOG.md, "Locked-rule validation".
export const ARV_CONFIDENCE_ALARM_THRESHOLD = 88;

/**
 * Why the alarm should fire, or null for "say nothing".
 *
 * @param {object} analysis      the analysis as the API returned it (may be null/partial)
 * @param {number} fallbackScore the screen's live confidence score, used only when the
 *                               payload carries none of its own
 * @param {number} threshold     score at or above which the score rule stays quiet
 * @returns {'sources_disagree'|'one_source_missing'|'wide_pool'|'weak_comps'|null}
 */
export function arvAlarmReason(analysis, fallbackScore, threshold = ARV_CONFIDENCE_ALARM_THRESHOLD) {
  const arvDetails = analysis?.arv_details || {};
  const arvStatus = arvDetails.arv_status;
  const arvConfidence = arvDetails.arv_confidence || analysis?.arv_confidence;

  // Do not stack two warnings: ARVWarningBanner already owns the LOW / UNABLE case. This
  // test is FIRST, so the gate cannot reintroduce the stacking it exists to prevent.
  if (arvConfidence === 'LOW' || arvStatus === 'UNABLE_TO_COMPUTE' || arvStatus === 'LOW_CONFIDENCE') {
    return null;
  }

  // The backend's disagreement gate, when it ran. Read defensively on purpose: it is ABSENT
  // on every analysis stored before it shipped and on every run with STABLE_COMP_POOL off.
  // Absent means "no opinion" -- never "confirmed" -- so those fall through to the score
  // rule below and behave exactly as they did before the gate existed.
  //
  // `=== false` and not `!gate?.point_value_supported`: undefined is not a refusal.
  const gate = arvDetails?.confidence_gate;
  if (gate?.point_value_supported === false) {
    if (gate?.reason === 'sources_disagree') return 'sources_disagree';
    if (gate?.reason === 'one_source_missing') return 'one_source_missing';
    // A reason this screen has no copy for. Fall through rather than borrow one of the two
    // above: each makes a specific factual claim ("more than X% apart", "only one source
    // available") and a new reason would be neither. New reason, new copy.
  }

  // The market around this property is scattered. Sits ABOVE the score rule because it
  // says WHY the value is soft, where 'weak_comps' only says that it is; and BELOW the
  // gate, which speaks about this value rather than about the area.
  //
  // This is what catches 1434 Oregon Ave: score 91, above the alarm threshold, 49% wrong.
  // Adding it took detection on the corpus from 5/6 to 6/6 with no new false positive.
  // Absent (null) is NO OPINION -- a pool too thin to describe cannot vouch for itself.
  if (arvDetails?.pool_dispersion?.wide === true) {
    return 'wide_pool';
  }

  // `??` and an explicit Number check, never `||`: a real score of 0 is a measurement, not
  // a missing value, and this screen has been bitten by that exact coercion before.
  const rawScore = arvDetails?.confidence?.score
    ?? analysis?.arv_confidence_score
    ?? fallbackScore;
  const score = Number(rawScore);

  // An ABSENT score is not evidence of a good one. Unknown warns.
  if (Number.isFinite(score) && score >= threshold) {
    return null;
  }
  return 'weak_comps';
}

/**
 * How loudly the screen should say it.
 *
 * 'warning' is reserved for `sources_disagree`, where two independent sources contradict
 * each other by more than the threshold. Everything else is a 'note'.
 *
 * WHY THE SPLIT EXISTS. Measured on the corpus, a note fires on 15 of 22 analyses -- 59%
 * of them `weak_comps`. A warning that appears two times in three stops being read, and
 * then it is not read on the one that matters either. The frequent cases describe the
 * AREA and invite the user to add what they know; the rare one reports a contradiction.
 * Dressing both as the same amber alarm spends the alarm on the common case.
 */
export function arvAlarmSeverity(reason) {
  return reason === 'sources_disagree' ? 'warning' : 'note';
}

/**
 * The threshold as the sentence should read it: "50", not "50.0"; "47.5" stays "47.5".
 * Returns null when the payload did not carry one, which the copy handles by not naming
 * a number at all.
 */
export function formatThreshold(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10);
}

/**
 * What the box says, and the two figures it shows (case A only).
 *
 * The percentage in the disagree copy comes from `gate.threshold_pct`, never a literal:
 * DISCREPANCY_THRESHOLD_PCT is calibrated on two properties in one ZIP and is expected to
 * be recalibrated, and a sentence hard-coding 50 would quietly become a lie the day it
 * moves. When the payload carries no threshold the sentence drops the number instead of
 * inventing one.
 *
 * @returns {{title: string, body: string, asking: number|null, closed: number|null}}
 */
export function arvAlarmContent(reason, gate, lang) {
  const en = lang === 'en';
  const pct = formatThreshold(gate?.threshold_pct);

  if (reason === 'sources_disagree') {
    const asking = Number(gate?.asking_value);
    const closed = Number(gate?.closed_value);
    const bothKnown = Number.isFinite(asking) && asking > 0
      && Number.isFinite(closed) && closed > 0;
    const differ = pct
      ? (en ? `They differ by more than ${pct}%` : `Difieren más del ${pct}%`)
      : (en ? 'They differ too much' : 'Difieren demasiado');
    return {
      title: en ? 'The two data sources do not agree' : 'Las dos fuentes de datos no coinciden',
      body: en
        ? `${differ}, so this value is not confirmed. Treat it as a rough range, not a figure to act on. Adding comparables you know is what narrows it.`
        : `${differ}, así que este valor no está confirmado. Tomalo como un rango aproximado, no como una cifra para decidir. Agregar comparables que conozcas es lo que lo ajusta.`,
      asking: bothKnown ? asking : null,
      closed: bothKnown ? closed : null,
    };
  }

  if (reason === 'one_source_missing') {
    return {
      title: en ? 'This value could not be cross-checked' : 'Este valor no se pudo contrastar',
      body: en
        ? 'To confirm it we compare two sources: asking prices and closed sales. In this area only one was available. If you know closed sales nearby, add them and the value rests on them.'
        : 'Para confirmarlo comparamos dos fuentes: precios pedidos y ventas cerradas. En esta zona solo había una. Si conocés ventas cerradas cerca, agregalas y el valor se apoya en ellas.',
      asking: null,
      closed: null,
    };
  }

  if (reason === 'wide_pool') {
    return {
      title: en ? 'Prices vary a lot in this area' : 'En esta zona los precios varían mucho',
      body: en
        ? 'Nearby sales run from very cheap to very expensive, so your local read counts for more than an average here. If you know sales on the block, add them and the value adjusts to them.'
        : 'Las ventas cercanas van de muy baratas a muy caras, así que acá tu criterio local pesa más que un promedio. Si conocés ventas de la cuadra, agregalas y el valor se ajusta a ellas.',
      asking: null,
      closed: null,
    };
  }

  // THE FREQUENT ONE -- 59% of analyses on the corpus. Rewritten from "Weak comparable
  // support / the comparables support it weakly ... not a figure to act on", which opened
  // on the defect, repeated it, and then told a professional not to use the number. At two
  // times in three that is the product disowning itself. The limit is still stated -- the
  // comparables do not quite agree -- but as a fact about the AREA, after what the reader
  // can do about it, and the remedy is concrete and true: the value is recomputed on the
  // comps they add.
  return {
    title: en
      ? 'Sharpen this value with what you know about the area'
      : 'Afiná este valor con tu conocimiento de la zona',
    body: en
      ? 'This value comes from the comparables available, and in this area they do not quite agree with each other. If you know similar sales nearby, add them and the value is recalculated with yours.'
      : 'Este valor sale de los comparables que hay disponibles, y en esta zona no terminan de coincidir entre sí. Si conocés ventas parecidas cerca, agregalas: el valor se recalcula con las tuyas.',
    asking: null,
    closed: null,
  };
}

/**
 * The one-line mark beside the ARV figure. Not a second notice: it repeats no copy.
 *
 * Two forms, because the two levels are not the same thing. The warning form names the
 * problem; the note form only points at the note. It deliberately does NOT say "add your
 * comps": the line directly above it in the card already reads "add comparables to change
 * it", and saying that twice under one number reads as nagging, not as a pointer.
 */
export function arvNotConfirmedMark(lang, severity = 'warning') {
  const en = lang === 'en';
  if (severity === 'note') {
    return en ? 'See the note on this area ↑' : 'Ver nota sobre la zona ↑';
  }
  return en
    ? 'Value not confirmed — see the notice above'
    : 'Valor no confirmado — ver aviso arriba';
}
